import React, {useMemo, useState} from 'react';
import {Country} from '../types';
import useMap from '../hooks/useMap';
import {twMerge} from 'tailwind-merge';

interface CountryProps {
    country: Country;
}

const CountryComponent: React.FC<CountryProps> = ({country}) => {
    const {
        fillColors,
        fillType,
        onClickHandlers,
        flagOnHover,
        cssClasses,
        setTooltip,
        tooltipConfig,
        defaultFillColor,
        defaultFillType,
        defaultOnClickHandler,
        defaultFlagOnHover,
        defaultCssClass,
    } = useMap();

    const [isFlagOnHover, setIsFlagOnHover] = useState<boolean>(false);

    const currentFill = useMemo<string>(() => {
        const type = fillType[country.alpha2Code] || defaultFillType;
        if (type === 'flag') {
            return `url(#${country.alpha2Code}-flag)`;
        }
        return fillColors[country.alpha2Code] || defaultFillColor;
    }, [fillColors, fillType, country.alpha2Code, defaultFillType, defaultFillColor]);

    const showFlagOnHover = useMemo<boolean>(() => {
        return flagOnHover[country.alpha2Code] !== undefined ? flagOnHover[country.alpha2Code] : defaultFlagOnHover;
    }, [flagOnHover, country.alpha2Code, defaultFlagOnHover]);

    const handleMouseEnter = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
        if (showFlagOnHover) {
            setIsFlagOnHover(true);
        }

        if (tooltipConfig.enabled) {
            const tooltipContent = tooltipConfig.renderContent(country);
            setTooltip({
                x: event.clientX,
                y: event.clientY,
                content: tooltipContent,
            });
        }
    };

    const handleMouseLeave = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
        if (showFlagOnHover) {
            setIsFlagOnHover(false);
        }

        if (tooltipConfig.enabled) {
            setTooltip(null);
        }
    };

    const handleClick = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
        const handler = onClickHandlers[country.alpha2Code] || defaultOnClickHandler;
        if (handler) {
            handler(country);
        }
    };

    const finalFill = useMemo<string>(() => {
        if (isFlagOnHover) {
            return `url(#${country.alpha2Code}-flag)`;
        }
        return currentFill;
    }, [isFlagOnHover, currentFill, country.alpha2Code]);

    const flagPattern = useMemo(() => {
        const type = fillType[country.alpha2Code] || defaultFillType;
        const shouldDisplayFlag = type === 'flag' || showFlagOnHover;
        if (shouldDisplayFlag) {
            return (
                <defs>
                    <pattern
                        id={`${country.alpha2Code}-flag`}
                        patternUnits="objectBoundingBox"
                        width="1"
                        height="1"
                        patternContentUnits="objectBoundingBox"
                    >
                        <image
                            href={country.flagUrl}
                            x="0"
                            y="0"
                            width="1"
                            height="1"
                            preserveAspectRatio="none"
                        />
                    </pattern>
                </defs>
            );
        }
        return null;
    }, [fillType, country.alpha2Code, country.flagUrl, showFlagOnHover, defaultFillType]);

    const cssClass = useMemo<string>(() => {
        const countryClass = cssClasses[country.alpha2Code] || '';
        return twMerge(
            country.alpha3Code.toLowerCase(),
            country.alpha2Code.toLowerCase(),
            country.continent.replace(' ', '-').toLowerCase(),
            'ring-0 outline-none',
            'focus:outline-none',
            defaultCssClass,
            countryClass
        );
    }, [cssClasses, country.alpha2Code, defaultCssClass]);

    const randomCode = Math.random().toString(36).substring(7);

    return (
        <>
            {flagPattern}
            <path
                d={country.svgPath}
                id={country.alpha2Code + randomCode}
                fill={finalFill}
                className={cssClass}
                style={{ pointerEvents: 'all', cursor: 'pointer' }} // Ensure pointer events and a pointer cursor
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                tabIndex={0}
                aria-label={`Country: ${country.commonName}`}
                data-code={country.alpha2Code}
                data-code3={country.alpha3Code}
                data-continent={country.continent}
                role="button"
                // Ensure pointer events and a visual cursor
                pointerEvents="visiblePainted"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClick(e);
                }}

            />
        </>
    );
};

export default React.memo(CountryComponent);
